import React from 'react';
import { IconCheck, IconHelp, IconLock, IconRefresh, IconTrash, IconZap } from '../Icons';
import { useBrandIdentity } from '../../utils/brandIdentity';

const getInfoPoints = (appName) => [
  {
    title: 'OAuth 2.0',
    body: `Your Tradovate password never touches ${appName} servers.`,
  },
  {
    title: 'Read-only scope',
    body: `${appName} can only read fills — not place or cancel orders.`,
  },
  {
    title: 'Tokens auto-refresh',
    body: 'Your connection stays live without re-authorizing when possible.',
  },
];

function ModeBadge({ mode, connected }) {
  if (!connected) return null;
  const isDemo = mode === 'demo';
  return (
    <span className={`vd-ninja-mode-badge${isDemo ? ' demo' : ' live'}`}>
      <IconCheck size={12} />
      {isDemo ? 'Demo connected' : 'Live connected'}
    </span>
  );
}

export default function NinjaTraderConnectPortal({
  busy = false,
  tradovateOAuthConfigured = false,
  isDemo = false,
  onDemoChange,
  onOAuthConnect,
  onUseApiKeys,
  connections = {},
  onSync,
  onRemove,
  error = '',
  message = '',
}) {
  const { appName } = useBrandIdentity();
  const live = connections.live || {};
  const demo = connections.demo || {};
  const activeMode = isDemo ? 'demo' : 'live';
  const activeConnection = connections[activeMode] || {};
  const activeConnected = Boolean(activeConnection.configured);
  const anyConnected = Boolean(live.configured || demo.configured);

  return (
    <div className="vd-ninja-connect">
      <div className="vd-ninja-connect-head">
        <h3>Connect NinjaTrader</h3>
        <p className="vd-muted">
          Authorize access on Tradovate&apos;s secure login page — no passwords stored here.
        </p>
      </div>

      {(live.configured || demo.configured) && (
        <div className="vd-ninja-mode-badges">
          <ModeBadge mode="live" connected={live.configured} />
          <ModeBadge mode="demo" connected={demo.configured} />
        </div>
      )}

      <div className="vd-ninja-connect-grid">
        <div className="vd-ninja-connect-panel vd-ninja-connect-panel-main">
          <span className="vd-control-label">Account Mode</span>
          <div className="vd-ninja-mode-toggle" role="group" aria-label="Tradovate account mode">
            <button
              type="button"
              className={`vd-ninja-mode-btn${!isDemo ? ' active' : ''}${live.configured ? ' connected' : ''}`}
              onClick={() => onDemoChange(false)}
              disabled={busy}
            >
              Live
              {live.configured ? <span className="vd-ninja-mode-btn-tag">Connected</span> : null}
            </button>
            <button
              type="button"
              className={`vd-ninja-mode-btn${isDemo ? ' active' : ''}${demo.configured ? ' connected' : ''}`}
              onClick={() => onDemoChange(true)}
              disabled={busy}
            >
              Demo / Sim
              {demo.configured ? <span className="vd-ninja-mode-btn-tag">Connected</span> : null}
            </button>
          </div>

          <div className="vd-ninja-security-note">
            <span className="vd-ninja-security-icon" aria-hidden="true">
              <IconLock size={18} />
            </span>
            <p>
              Clicking <strong>Connect with NinjaTrader</strong> opens a secure Tradovate login page.
              You log in directly — {appName} never sees your password.
            </p>
          </div>

          {tradovateOAuthConfigured ? (
            <button
              type="button"
              className="vd-btn vd-ninja-connect-btn"
              disabled={busy}
              onClick={onOAuthConnect}
            >
              <IconZap size={18} />
              {busy ? 'Redirecting…' : (activeConnected ? `Reconnect ${isDemo ? 'Demo' : 'Live'}` : 'Connect with NinjaTrader')}
            </button>
          ) : (
            <p className="vd-muted vd-ninja-oauth-unavailable">
              Tradovate OAuth is not configured on this server. Use API key credentials instead.
            </p>
          )}

          <button
            type="button"
            className="vd-ninja-alt-link"
            onClick={onUseApiKeys}
            disabled={busy}
          >
            Use API key credentials instead
          </button>
        </div>

        <aside className="vd-ninja-connect-panel vd-ninja-connect-panel-info">
          <div className="vd-ninja-info-brand">
            <span className="vd-broker-logo ninjatrader has-image sm" aria-hidden="true">
              <img src="/brokers/ninjatrader.jpg" alt="" />
            </span>
            <div>
              <strong>NinjaTrader</strong>
              <span className="vd-muted">Connecting NinjaTrader</span>
            </div>
          </div>

          <div className="vd-ninja-info-heading">
            <span>How it works</span>
            <span className="vd-ninja-info-help" title={`NinjaTrader futures accounts clear through Tradovate. ${appName} reads your executed fills via Tradovate OAuth or API keys. You can connect both live and demo accounts separately.`}>
              <IconHelp size={14} />
            </span>
          </div>

          <ul className="vd-ninja-info-list">
            {getInfoPoints(appName).map((point) => (
              <li key={point.title}>
                <span className="vd-ninja-info-bullet" aria-hidden="true" />
                <span>
                  <strong>{point.title}</strong>
                  {' — '}
                  {point.body}
                </span>
              </li>
            ))}
          </ul>

          {anyConnected && (
            <p className="vd-muted vd-ninja-dual-hint">
              Connect the other mode above to track both live and sim accounts in Portfolio Tracker (Broker Sync).
            </p>
          )}
        </aside>
      </div>

      {error && <p className="vd-error">{error}</p>}
      {message && <p className="vd-success">{message}</p>}

      {activeConnected && (
        <footer className="vd-broker-detail-footer vd-ninja-connect-footer">
          <button type="button" className="vd-btn danger" onClick={onRemove} disabled={busy}>
            <IconTrash size={15} /> Remove {isDemo ? 'Demo' : 'Live'} account
          </button>
          <div className="vd-broker-detail-footer-actions">
            <button type="button" className="vd-btn" onClick={onSync} disabled={busy}>
              {busy ? 'Syncing…' : (
                <>
                  <IconRefresh size={16} /> Sync {isDemo ? 'Demo' : 'Live'}
                </>
              )}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}
